<template>
  <button
    ref="dropTarget"
    class="avatar"
    type="button"
    aria-hidden="true"
    :disabled="!canUpload"
    :class="{isDropping, isPortrait}"
    @click="handleClick"
  >
    <img v-if="avatarUrl" class="image" :src="avatarUrl" :style="imgStyle" alt="" />
    <span v-if="showAddLabel && !hasCustomImage" class="add-image-label">Add<br>profile<br>image</span>
  </button>
</template>

<script setup>
import {ref, computed} from "vue";
import {useRouter} from "vue-router";
import {useDropZone, useFileDialog} from "@vueuse/core";
import {useLog} from "@/composables/useLog";
import {useAvatarCropState} from "@/composables/useAvatarCropState";
import {
  PORTRAIT_ASPECT, getPortraitRect, getRectFromCircle, getRectStyle,
} from "@/composables/portraitCrop";

const log = useLog("ProfileAvatar");
// isPortrait: a People-thumbnail rectangle (Testimonial) instead of the circle (Settings)
const props = defineProps({
  user: {type: Object, default: null},
  showAddLabel: Boolean,
  isPortrait: Boolean
});

const router = useRouter();
const dropTarget = ref(null);
const {setFile} = useAvatarCropState();

const canUpload = computed(() => {
  if (!props.user) return false;
  if (!props.user?.setProfileImage) return false;
  return typeof props.user?.setProfileImage === "function";
});

const hasCustomImage = computed(() => !!props.user?.hasImage);

const avatarUrl = computed(() => {
  const url = props.user?.imageUrl;
  if (!url) return;
  // no cache-buster: Clerk gives each new photo its own imageUrl, and its images may be cached
  // for a month (immutable), so switching tabs reuses the browser's copy
  return `${url}?width=112&quality=70`;
});

// the circle, or the portrait rectangle (saved, else estimated from the circle): portraitCrop.js
function getRect(unsafeMetadata) {
  if (props.isPortrait) return getPortraitRect(unsafeMetadata);
  const circle = unsafeMetadata?.avatarCrop;
  return circle?.w ? getRectFromCircle(circle, 1) : null;
}

const imgStyle = computed(() => {
  const rect = props.user?.hasImage ? getRect(props.user?.unsafeMetadata) : null;
  if (!rect) return {width: "100%", height: "100%", objectFit: "cover"};
  return getRectStyle(rect, props.isPortrait ? PORTRAIT_ASPECT : 1);
});

function isImageFile(file) {
  const type = String(file?.type || "");
  if (type.startsWith("image/")) return true;
  const name = String(file?.name || "").toLowerCase();
  return !!name.match(/\.(png|jpg|jpeg|gif|webp|bmp|tiff|tif|svg)$/);
}

function getFileArray(files) {
  if (!files) return [];
  if (Array.isArray(files)) return files;
  if (typeof files.length === "number") return Array.from(files);
  return [];
}

function getFirstImageFile(files) {
  const imageFiles = getFileArray(files).filter(isImageFile);
  return imageFiles[0] || null;
}

function handleSelectedImage(file) {
  if (!file) return;
  setFile(file);
  log(`crop pending: name=${file.name}`);
  router.push({hash: "#crop-image"});
}

function handleFiles(files) {
  const file = getFirstImageFile(files);
  if (!file) return log("no image file found");
  handleSelectedImage(file);
}

const {open: openFileDialog, onChange} = useFileDialog({accept: "image/*", multiple: false});
onChange((files) => handleFiles(files));

// the Testimonial photo (isPortrait) crops the photo already there; otherwise a new upload
function handleClick() {
  if (!canUpload.value) return log("avatar click ignored: not ready");
  if (props.isPortrait && hasCustomImage.value) return router.push({hash: "#crop-portrait"});
  openFileDialog();
}

const {isOverDropZone: isDropping} = useDropZone(dropTarget, {
  onDrop(files, event) {
    if (!canUpload.value) return log("drop ignored: not ready");
    const types = Array.isArray(event?.dataTransfer?.types) ? event.dataTransfer.types : [];
    log(`drop: types=${types.join(",") || "none"} count=${files?.length || 0}`);
    handleFiles(files);
  },
  multiple: false,
  preventDefaultForUnhandled: true,
});
</script>

<style scoped>
.avatar {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 999px;
  overflow: hidden;
  display: grid;
  place-items: center;
  position: relative;
  background: radial-gradient(circle at 30% 25%, #8b5cf6, #f59e0b);
  border: 1px solid var(--color-mute, #ccc);
  padding: 0;
  cursor: pointer;
  container-type: inline-size;
}
.avatar.isPortrait {
  aspect-ratio: 167 / 241; /* PORTRAIT_ASPECT */
  border-radius: 0;
}
.avatar:disabled {
  cursor: default;
  opacity: 0.7;
}
.avatar.isDropping {
  outline: 2px solid rgba(255, 255, 255, 0.75);
  outline-offset: 2px;
}
.image {
  display: block;
}
.add-image-label {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  font-size: 20cqi;
  line-height: 1.4;
  text-align: center;
  background: rgba(0, 0, 0, 0.5);
}
/* opens the crop dialog: a little darker on hover (DESIGN.md § 15) */
.avatar:not(:disabled):hover {
  filter: brightness(0.92);
}
</style>
